<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import type { LocaleCode } from '@/shared/i18n/messages';
import { currentLocale, localesForCurrentSkin, setLocale, t } from '@/shared/i18n/messages';
import { oroMxAssetPaths } from '@/shared/theme/oro-mx-home-data';
import { oroMxLangFlags } from '@/shared/theme/oro-mx-page-data';

/** `footer` only switches trigger markup (arrow box). Full-width bar CSS lives on OroMxPageFooter. */
const props = withDefaults(
  defineProps<{
    variant?: 'menu' | 'footer';
  }>(),
  { variant: 'menu' },
);

const emit = defineEmits<{
  openChange: [open: boolean];
}>();

const open = ref(false);
const root = ref<HTMLElement | null>(null);
const locales = computed(() => localesForCurrentSkin());
const active = computed(
  () => locales.value.find((item) => item.code === currentLocale.value) ?? locales.value[0],
);
const isFooter = computed(() => props.variant === 'footer');
const footerArrow = oroMxAssetPaths.footerLangArrow;

function toggle() {
  open.value = !open.value;
}

function close() {
  open.value = false;
}

function choose(code: LocaleCode) {
  setLocale(code);
  close();
}

function flagSrc(code: LocaleCode) {
  return oroMxLangFlags[code];
}

function onDocumentClick(event: MouseEvent) {
  if (root.value && !root.value.contains(event.target as Node)) close();
}

watch(open, (isOpen) => {
  emit('openChange', isOpen);
  if (isOpen) document.addEventListener('click', onDocumentClick, true);
  else document.removeEventListener('click', onDocumentClick, true);
});

onBeforeUnmount(() => document.removeEventListener('click', onDocumentClick, true));
</script>

<template>
  <div ref="root" class="oro-lang" :class="[`oro-lang--${props.variant}`, { 'is-open': open }]">
    <button class="oro-lang__trigger" type="button" :aria-expanded="open" @click="toggle">
      <img
        v-if="flagSrc(active.code)"
        class="oro-lang__flag"
        :src="flagSrc(active.code)"
        alt=""
      />
      <span v-else class="oro-lang__emoji" aria-hidden="true">{{ active.flag }}</span>
      <span class="oro-lang__code">{{ t(`oro.lang.short.${active.code}`) }}</span>
      <span v-if="isFooter" class="oro-lang__arrow-box" :class="{ 'is-open': open }">
        <img class="oro-lang__arrow-img" :src="footerArrow" alt="" />
      </span>
      <i v-else class="oro-lang__arrow" :class="{ 'is-open': open }" />
    </button>
    <ul v-if="open" class="oro-lang__menu" role="listbox">
      <li v-for="locale in locales" :key="locale.code">
        <button
          class="oro-lang__option"
          :class="{ 'is-active': locale.code === currentLocale }"
          type="button"
          role="option"
          :aria-selected="locale.code === currentLocale"
          @click="choose(locale.code)"
        >
          <img
            v-if="flagSrc(locale.code)"
            class="oro-lang__flag"
            :src="flagSrc(locale.code)"
            alt=""
          />
          <span v-else class="oro-lang__emoji" aria-hidden="true">{{ locale.flag }}</span>
          <span>{{ locale.label }}</span>
        </button>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.oro-lang {
  position: relative;
  width: auto;
}

.oro-lang__trigger {
  display: flex;
  gap: 0.4267rem;
  align-items: center;
  width: auto;
  min-width: 4.2rem;
  height: 2.0267rem;
  padding: 0.4267rem 0.5333rem;
  color: #fff;
  font-size: 0.64rem;
  font-weight: 600;
  white-space: nowrap;
  background: #15161c;
  border: 0;
  border-radius: 0.32rem;
}

.oro-lang__flag {
  display: block;
  width: 1.1733rem;
  height: 1.1733rem;
  object-fit: cover;
  border-radius: 0.1067rem;
}

.oro-lang__emoji {
  display: grid;
  place-items: center;
  width: 1.1733rem;
  height: 1.1733rem;
  font-size: 0.8rem;
  line-height: 1;
}

.oro-lang__code {
  white-space: nowrap;
}

.oro-lang__arrow {
  display: block;
  width: 0.64rem;
  height: 0.64rem;
  margin-left: auto;
  background: no-repeat center / contain
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23fff' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
}

.oro-lang__arrow.is-open {
  transform: rotate(180deg);
}

.oro-lang__menu {
  position: absolute;
  right: 0;
  bottom: calc(100% + 0.32rem);
  z-index: 40;
  min-width: 8.8rem;
  margin: 0;
  padding: 0.2133rem;
  list-style: none;
  background: #15161c;
  border: 1px solid #2a2b31;
  border-radius: 0.32rem;
}

.oro-lang__option {
  display: flex;
  gap: 0.4267rem;
  align-items: center;
  width: 100%;
  min-height: 1.8133rem;
  padding: 0 0.4267rem;
  color: #c8c8cc;
  font-size: 0.64rem;
  text-align: left;
  white-space: nowrap;
  background: transparent;
  border: 0;
}

.oro-lang__option.is-active,
.oro-lang__option:hover {
  color: #fff;
  background: #232325;
}
</style>
